<template>
  <div class="app-container">
    <!-- 页面头部 -->
    <section class="models-recommend-header">
      <div class="container">
        <div class="header-top">
          <h1>{{ activeTitle === 'hot' ? '推荐模型' : '我的关注模型' }}</h1>
          <p class="header-desc">共{{ totalCount }}个模型，精选优质3D数字资产</p>
        </div>

        <!-- 二级分类筛选 -->
        <div class="section-tabs" v-if="filterList.length > 0">
          <button class="tab-btn" :class="{ active: activeTab === '全部' }" @click="handleTabChange('全部')">
            全部
          </button>
          <button class="tab-btn" :class="{ active: activeTab === filter.name }" v-for="filter in filterList"
            :key="filter.id" @click="handleTabChange(filter.name, filter.id)">
            {{ filter.name }}
          </button>
        </div>
      </div>
    </section>

    <!-- 核心模型展示区 -->
    <section class="models-section">
      <div class="container">
        <!-- 首次加载骨架屏 -->
        <div v-if="isLoading" class="skeleton-container">
          <div class="skeleton-card" v-for="i in 12" :key="i"></div>
        </div>

        <!-- 模型列表（懒加载显示） -->
        <div class="models-grid" v-else>
          <div class="model-card" v-for="model in displayedModels" :key="model.id" @click="handleModelDetail(model.id)">
            <div class="model-img-container" @click="goToDetail(model.id)">
              <img :src="model.img" :alt="model.name" class="model-img" />
              <!-- 原有：右上角独家发售标签 -->
              <div class="model-tag" v-if="model.isExclusive">独家发售</div>
              <!-- 新增：左上角官方认证标签，基于officialmod字段判断 -->
              <div class="model-official-tag" v-if="model.officialmod">官方认证</div>
              <!-- 新增：可开票标签 -->
              <div class="model-invoice-tag" v-if="model.whetherInvoice">可开发票</div>
            </div>
            <div class="model-info">
              <h3 class="model-name">{{ model.name }}</h3>
              <div class="model-meta">
                <span class="model-price">{{ model.price }}云币</span>
                <span class="model-sales">已售{{ model.sales }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 无数据提示 -->
        <div v-if="!isLoading && filteredAllModels.length === 0" class="no-data">
          {{ activeTitle === 'hot' ? '暂无热门模型数据' : '暂无关注模型数据' }}
        </div>

        <!-- 滚动加载中提示 -->
        <div v-if="isAppendLoading" class="loading">加载中...</div>

        <!-- 无更多数据提示 -->
        <div v-if="!hasMore && displayedModels.length > 0" class="no-more">已加载全部模型</div>
      </div>
    </section>
  </div>
</template>

<script>
import Top from '../../components/Header.vue';
import Bottom from '../../components/Footer.vue';

export default {
  name: 'AllModelsRecommend',
  components: {
    Top,
    Bottom
  },
  data() {
    return {
      token: localStorage.getItem('token'),
      activeTitle: 'hot', // 热门/我的关注切换
      activeTab: '全部', // 二级分类筛选
      activeTypeId: '', // 当前选中的分类ID（对应onetype）
      filterList: [], // 分类筛选列表
      allModels: [], // 存储全部模型数据
      displayedModels: [], // 当前显示的模型列表
      totalCount: 0, // 模型总数
      isLoading: true, // 首次加载状态
      isAppendLoading: false, // 滚动追加加载状态
      pageSize: 10, // 每次加载数量
      currentBatch: 1, // 当前加载批次
      hasMore: true, // 是否还有更多数据
      scrollDebounceTimer: null, // 滚动防抖计时器
      scrollHandler: null // 滚动事件处理函数引用
    };
  },
  computed: {
    // 筛选后的全部模型（仅用于滚动加载）
    filteredAllModels() {
      return this.allModels;
    }
  },
  watch: {
    // 切换一级标签时重新加载数据
    activeTitle: {
      immediate: true,
      handler() {
        this.resetPageState();
        this.fetchAllModels();
      }
    }
  },
  mounted() {
    // 1. 解决页面滚动阻塞问题：强制设置html/body允许滚动
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.body.style.height = 'auto';

    // 获取分类筛选列表
    this.getFilterList();

    // 2. 确保DOM就绪后绑定滚动事件，存储函数引用
    this.$nextTick(() => {
      this.scrollHandler = this.handleScrollLazyLoad.bind(this); // 绑定作用域
      window.addEventListener('scroll', this.scrollHandler);
    });
  },
  beforeDestroy() {
    // 3. 正确移除滚动事件（使用存储的函数引用）
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    clearTimeout(this.scrollDebounceTimer);
  },
  methods: {
    // 重置页面状态
    resetPageState() {
      this.isLoading = true;
      this.allModels = [];
      this.displayedModels = [];
      this.currentBatch = 1;
      this.hasMore = true;
      this.totalCount = 0;
    },

    // 映射API数据到本地格式
    mapApiDataToLocal(apiItem) {
      return {
        id: apiItem.modelId,
        name: apiItem.title,
        img: apiItem.previewUrl || apiItem.coverUrl?.[0] || '',
        price: apiItem.licensingPrices || apiItem.prices || 0,
        sales: apiItem.salesVolume || 0,
        category: apiItem.category || "全部",
        isExclusive: apiItem.isExclusive || false,
        officialmod: apiItem.officialmod || false,
        whetherInvoice: apiItem.whetherInvoice || false // 可开票字段
      };
    },

    // 获取分类筛选列表
    async getFilterList() {
      try {
        const res = await this.$apiFun.getOneType();
        if (res.status === 200) {
          this.filterList = res.obj.map(item => ({
            id: item.oneId, // onetype对应的值
            name: item.oneName,
            img: item.omeImage
          }));
        }
      } catch (err) {
        console.error('获取分类列表失败:', err);
      }
    },

    // 获取全部模型数据 - 增加onetype参数
    async fetchAllModels() {
      this.isLoading = true;
      try {
        let res;
        // 根据一级标签调用不同接口
        if (this.activeTitle === 'hot') {
          // 热门模型接口 - 传递onetype参数
          const params = {
            onetype: this.activeTab,
          };
          res = await this.$apiFun.getRecommendedPopular(params);
        } else {
          // 我的关注模型接口 - 如果需要也可以传递onetype参数
          res = await this.$apiFun.getMyInterest();
          // 未登录处理
          if (res.status === 401) {
            localStorage.removeItem('userData');
            localStorage.removeItem('token');
            this.token = '';
            this.$router.push('/login');
            return;
          }
        }

        if (res.status === 200) {
          this.allModels = res.obj.map(item => this.mapApiDataToLocal(item));
          this.totalCount = this.allModels.length;
          // 初始化显示第一批数据
          this.displayedModels = this.getBatchData(1);
          // 准确判断是否有更多数据
          this.hasMore = this.filteredAllModels.length > this.displayedModels.length;
        }
      } catch (err) {
        console.error(`加载${this.activeTitle === 'hot' ? '热门模型' : '我的关注模型'}失败:`, err);
        this.$message.error('加载模型数据失败，请稍后重试');
      } finally {
        this.isLoading = false;
      }
    },

    // 获取指定批次的模型数据
    getBatchData(batchNum) {
      const startIndex = (batchNum - 1) * this.pageSize;
      const endIndex = batchNum * this.pageSize;
      return this.filteredAllModels.slice(startIndex, endIndex);
    },

    // 滚动懒加载处理（防抖）
    handleScrollLazyLoad() {
      clearTimeout(this.scrollDebounceTimer);
      this.scrollDebounceTimer = setTimeout(() => {
        // 严格过滤无效状态
        if (this.isLoading || this.isAppendLoading || !this.hasMore) {
          return;
        }

        // 兼容所有浏览器的滚动参数获取
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
        const clientHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

        // 降低触发阈值到50px，更容易触发
        const triggerThreshold = 50;
        const isReachBottom = scrollTop + clientHeight >= scrollHeight - triggerThreshold;

        if (isReachBottom) {
          this.appendNextBatch();
        }
      }, 300);
    },

    // 追加加载下一批模型
    appendNextBatch() {
      if (this.isAppendLoading || !this.hasMore) {
        return;
      }

      this.isAppendLoading = true;
      this.currentBatch++;

      setTimeout(() => {
        const nextBatchData = this.getBatchData(this.currentBatch);
        this.displayedModels = [...this.displayedModels, ...nextBatchData];
        // 更新是否有更多数据
        this.hasMore = this.displayedModels.length < this.filteredAllModels.length;
        this.isAppendLoading = false;
      }, 500);
    },

    // 一级标签切换（热门/我的关注）
    handleTitleChange(type) {
      if (type === 'followed' && !this.token) {
        this.$message.warning('请先登录后查看我的关注');
        this.$router.push('/login');
        return;
      }
      this.activeTitle = type;
    },

    // 二级分类切换 - 修改核心逻辑：调用接口获取数据
    handleTabChange(tab, typeId = '') {
      this.activeTab = tab;
      this.activeTypeId = typeId; // 存储对应的onetype值
      this.resetPageState(); // 重置页面状态
      this.fetchAllModels(); // 调用接口重新获取数据
    },

    // 模型详情跳转
    goToDetail(modelId) {
      this.$router.push({
        path: '/detail',
        query: { id: modelId }
      });
    },

    // 查看模型详情（预留）
    handleModelDetail(modelId) {
      console.log("查看模型详情，ID：", modelId);
    }
  }
};
</script>

<style lang="less" scoped>
// 原有样式保持不变
@base-deep: #0f1118;
@base-darker: #080a12;
@primary: #0ea5e9;
@primary-light: #38bdf8;
@accent: #7c3aed;
@text-primary: #fff;
@text-secondary: #94a3b8;
@border-color: rgba(148, 163, 184, 0.1);
@skeleton-bg: rgba(30, 41, 59, 0.5);

/* 全局容器：确保不阻塞页面滚动 */
.app-container {
  background-color: @base-deep;
  color: @text-primary;
  min-height: 100vh;
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  padding: 0 15px;
  overflow: visible;
  box-sizing: border-box;
}

/* 全局样式：确保html/body可以滚动（穿透scoped） */
:deep(html),
:deep(body) {
  overflow: auto !important;
  height: auto !important;
  margin: 0;
  padding: 0;
}

.container {
  width: 100%;
  max-width: 1785px;
  margin: 0 auto;
  padding: 0 15px;
}

/* 页面头部 */
.models-recommend-header {
  background-color: @base-darker;
  padding: 2rem 0;
  margin: 0 0 2rem;
  box-shadow: 0 2px 15px rgba(14, 165, 233, 0.1);

  .header-top {
    margin-bottom: 1.5rem;

    h1 {
      font-size: 2rem;
      color: @text-primary;
      margin-bottom: 0.5rem;
      text-shadow: 0 0 10px rgba(14, 165, 233, 0.2);
    }

    .header-desc {
      color: @text-secondary;
      font-size: 1rem;
      opacity: 0.9;
    }
  }

  // 二级分类标签样式
  .section-tabs {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;

    .tab-btn {
      padding: 0.5rem 1rem;
      color: @text-secondary;
      border: 1px solid rgba(14, 165, 233, 0.2);
      background: @base-darker;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s;

      &.active {
        background-color: @primary;
        color: #fff;
        border-color: @primary;
        box-shadow: 0 2px 8px rgba(14, 165, 233, 0.3);
      }

      &:hover:not(.active) {
        border-color: @primary;
        color: @primary-light;
        background-color: rgba(14, 165, 233, 0.05);
      }
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem 0;

    .header-top h1 {
      font-size: 1.5rem;
    }

    .section-tabs .tab-btn {
      padding: 0.3rem 0.8rem;
      font-size: 0.9rem;
    }
  }
}

/* 模型展示区 */
.models-section {
  width: 100%;
  margin: 0 auto 3rem;
  overflow: visible !important;
  /* 不阻塞滚动 */

  // 骨架屏样式
  .skeleton-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(clamp(220px, 100%/4, 280px), 1fr));
    gap: clamp(1rem, 2vw, 1.5rem);
  }

  .skeleton-card {
    background-color: @skeleton-bg;
    border-radius: 8px;
    overflow: hidden;
    height: 320px;
    animation: skeleton-loading 1.5s infinite alternate;
  }

  @keyframes skeleton-loading {
    0% {
      background-color: @skeleton-bg;
    }

    100% {
      background-color: rgba(45, 65, 94, 0.5);
    }
  }

  // 模型列表
  .models-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(clamp(220px, 100%/4, 280px), 1fr));
    gap: clamp(1rem, 2vw, 1.5rem);
    margin-bottom: 2rem;
  }

  .model-card {
    background-color: @base-darker;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    transition: all 0.3s;
    cursor: pointer;
    border: 1px solid @border-color;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 20px rgba(14, 165, 233, 0.15);
      border-color: rgba(14, 165, 233, 0.2);
    }

    .model-img-container {
      position: relative;
      width: 100%;
      aspect-ratio: 4/3;
      overflow: hidden;

      .model-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.5s ease;
        filter: brightness(0.7);

        &:hover {
          transform: scale(1.05);
          filter: brightness(0.9);
        }
      }

      .model-tag {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        background: linear-gradient(135deg, @primary, @accent);
        color: #fff;
        font-size: 0.8rem;
        padding: 0.2rem 0.5rem;
        border-radius: 2px;
        box-shadow: 0 2px 4px rgba(14, 165, 233, 0.3);
      }

      // 官方认证标签
      .model-official-tag {
        position: absolute;
        top: 0.5rem;
        left: 0.5rem;
        background: rgba(245, 158, 11, 0.9);
        color: #fff;
        font-size: 0.8rem;
        padding: 0.2rem 0.5rem;
        border-radius: 2px;
        white-space: nowrap;
        z-index: 10;
      }

      // 可开票标签
      .model-invoice-tag {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        background: linear-gradient(135deg, #e53935, #7c3aed);
        color: #fff;
        font-size: 0.8rem;
        padding: 0.2rem 0.5rem;
        border-radius: 2px;
        white-space: nowrap;
        z-index: 10;
      }
    }

    .model-info {
      padding: 1rem;

      .model-name {
        font-size: 1rem;
        color: @text-primary;
        margin-bottom: 0.5rem;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .model-meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: 0.5rem;
        border-top: 1px solid @border-color;

        .model-price {
          color: @primary-light;
          font-weight: 600;
        }

        .model-sales {
          color: @text-secondary;
          font-size: 0.9rem;
        }
      }
    }
  }

  // 无数据提示
  .no-data {
    text-align: center;
    padding: 3rem 0;
    color: @text-secondary;
    font-size: 1.2rem;
  }

  // 加载中提示
  .loading {
    text-align: center;
    padding: 2rem 0;
    color: @primary-light;
    font-size: 1rem;
  }

  // 无更多数据提示
  .no-more {
    text-align: center;
    padding: 1rem 0;
    color: @text-secondary;
    font-size: 0.9rem;
  }
}

// 响应式适配
@media (max-width: 1200px) {
  .models-grid {
    grid-template-columns: repeat(auto-fill, minmax(clamp(220px, 33.33%, 280px), 1fr));
  }
}

@media (max-width: 768px) {
  .models-grid {
    grid-template-columns: repeat(auto-fill, minmax(clamp(220px, 50%, 280px), 1fr));
  }
}

@media (max-width: 576px) {
  .models-grid {
    grid-template-columns: 1fr;
  }
}
</style>